import React, { useState, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';

export default function OutStationZone({ isOpen, onClose, onSubmit, editZone }) {
  const [formData, setFormData] = useState({
    name: '',
    multiplier: '',
    description: '',
    isFarFlung: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', multiplier: '', description: '', isFarFlung: false });
      setErrors({});
    } else if (editZone) {
      setFormData({
        name: editZone.zone_name || '',
        multiplier: editZone.multiplier || '',
        description: editZone.region_description || '',
        isFarFlung: editZone.isFarFlung || false
      });
      setErrors({});
    }
  }, [isOpen, editZone]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'multiplier') {
      const regex = /^\d*\.?\d*$/;
      if (regex.test(value) || value === '') {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value === 'true' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Zone name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Zone name must be at least 2 characters long';
    }

    if (!formData.multiplier) {
      newErrors.multiplier = 'Multiplier is required';
    } else if (isNaN(parseFloat(formData.multiplier)) || parseFloat(formData.multiplier) <= 0) {
      newErrors.multiplier = 'Multiplier must be a positive number';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        zone_name: formData.name.trim(),
        region_description: formData.description.trim(),
        multiplier: parseFloat(formData.multiplier),
        isFarFlung: formData.isFarFlung
      };

      onSubmit(submitData, editZone?.id);
      setFormData({ name: '', multiplier: '', description: '', isFarFlung: false });
      setErrors({});
    }
  };

  const isFormValid = formData.name.trim().length >= 2 &&
    formData.multiplier &&
    !isNaN(parseFloat(formData.multiplier)) &&
    parseFloat(formData.multiplier) > 0 &&
    formData.description.trim().length >= 5;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-8 rounded-lg w-1/3 max-h-[80vh] relative overflow-hidden">
        <button
          className="absolute top-4 right-4 text-black hover:text-gray-600 transition-colors"
          onClick={onClose}
        >
          <IoClose className="w-6 h-6" />
        </button>

        <h3 className="text-xl font-bold mt-2 text-gray-800">
          {editZone ? "Edit Out Station Zone" : "Add Out Station Zone"}
        </h3>

        <form onSubmit={handleSubmit} className="h-full">
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Out Station Zone Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all ${errors.name
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
                }`}
              placeholder="Enter out station zone name"
              required
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Out Station Multiplier <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="multiplier"
              value={formData.multiplier}
              onChange={handleInputChange}
              className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all ${errors.multiplier
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
                }`}
              placeholder="e.g., 1.2, 1.6, 3, 4"
              required
            />
            {errors.multiplier && (
              <p className="text-red-500 text-sm mt-1">{errors.multiplier}</p>
            )}
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Out Station Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 transition-all resize-none ${errors.description
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
                }`}
              placeholder="Enter out station zone description"
              required
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Far Flung
            </label>
            <div className="flex space-x-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isFarFlung"
                  value="true"
                  checked={formData.isFarFlung === true}
                  onChange={handleInputChange}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isFarFlung"
                  value="false"
                  checked={formData.isFarFlung === false}
                  onChange={handleInputChange}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="btn btn-reset-filter justify-center"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className={`btn justify-center ${
                editZone ? "btn-edit text-white" : "btn-primary"
              } ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {editZone ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}